import { useEffect, useRef, type ReactNode } from 'react'
import { Icon } from './Icon'

/** Accessible dialog: Escape closes, a backdrop click closes, focus lands on the
 *  first field on open and returns to whatever opened it on close. */
export function Modal({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  width = 520,
  variant = 'dialog',
  className = '',
}: {
  open: boolean
  title: string
  subtitle?: ReactNode
  onClose: () => void
  /** Optional: a dialog that only asks a question renders head and footer alone,
   *  rather than an empty band between them. */
  children?: ReactNode
  footer?: ReactNode
  width?: number
  variant?: 'dialog' | 'drawer'
  className?: string
}) {
  const panel = useRef<HTMLDivElement>(null)
  // A ref, so a new onClose identity on every parent render doesn't re-run the
  // effect and yank focus back to the first field mid-typing.
  const close = useRef(onClose)
  close.current = onClose

  useEffect(() => {
    if (!open) return
    const opener = document.activeElement as HTMLElement | null
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current()
      if (e.key === 'Tab') {
        const fields = Array.from(panel.current?.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex="0"]') ?? []).filter(el => el.getClientRects().length)
        const first = fields[0], last = fields[fields.length - 1]
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus() }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus() }
      }
    }
    document.addEventListener('keydown', onKey)
    requestAnimationFrame(() =>
      panel.current
        ?.querySelector<HTMLElement>('input:not([type=hidden]), textarea, select, .modal-body button')
        ?.focus(),
    )
    return () => {
      document.removeEventListener('keydown', onKey)
      opener?.focus?.()
    }
  }, [open])

  if (!open) return null
  return (
    <div
      className={`modal-backdrop${variant === 'drawer' ? ' drawer-backdrop' : ''}`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close.current()
      }}
    >
      <div
        className={`modal${variant === 'drawer' ? ' modal-drawer' : ''}${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        ref={panel}
        style={{ width }}
      >
        <header className="modal-head">
          <div>
            <h2 id="modal-title" className="h2">{title}</h2>
            {subtitle ? <p className="modal-sub">{subtitle}</p> : null}
          </div>
          <button className="icon-btn-plain" onClick={() => close.current()} aria-label="Close">
            <Icon name="close" size={20} />
          </button>
        </header>
        {children ? <div className="modal-body">{children}</div> : null}
        {footer ? <footer className="modal-foot">{footer}</footer> : null}
      </div>
    </div>
  )
}
